import { useMemo, useState } from "react";
import { DataTable } from "@/shared/components/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog";
import { useCollectionsE2EHistoryColumns } from "../hooks/useCollectionsE2EColumns";
import type { CollectionsE2EAssetRow, E2ETestHistoryRow } from "../types/collection-types";
import type { SortingState } from "@tanstack/react-table";

export function CollectionsE2EAssetDetailsDialog({
    open,
    onOpenChange,
    asset,
    history,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    asset?: CollectionsE2EAssetRow;
    history: E2ETestHistoryRow[];
}) {
    const columns = useCollectionsE2EHistoryColumns();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sorting, setSorting] = useState<SortingState>([]);

    // 정렬 적용
    const sortedHistory = useMemo(() => {
        if (sorting.length === 0) return history;

        const sorted = [...history];
        const sort = sorting[0];
        
        sorted.sort((a, b) => {
            const aValue = a[sort.id as keyof E2ETestHistoryRow];
            const bValue = b[sort.id as keyof E2ETestHistoryRow];

            if (aValue === bValue) return 0;
            if (aValue == null) return 1;
            if (bValue == null) return -1;

            let comparison = 0;
            if (typeof aValue === "string" && typeof bValue === "string") {
                comparison = aValue.localeCompare(bValue);
            } else if (typeof aValue === "number" && typeof bValue === "number") {
                comparison = aValue - bValue;
            } else {
                comparison = String(aValue).localeCompare(String(bValue));
            }

            return sort.desc ? -comparison : comparison;
        });

        return sorted;
    }, [history, sorting]);

    // 페이지네이션 적용
    const paginatedHistory = useMemo(() => {
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return sortedHistory.slice(startIndex, endIndex);
    }, [sortedHistory, page, pageSize]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl max-h-[85vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>E2E 테스트 상세 결과</DialogTitle>
                </DialogHeader>

                {asset && (
                    <div className="space-y-1 text-sm flex-shrink-0">
                        <div className="font-semibold">채널: {asset.channel}</div>
                        <div className="font-semibold">자산명: {asset.asset_name}</div>
                        <div>DW Account ID: {asset.dw_account_id}</div>
                    </div>
                )}

                <div className="flex-1 overflow-auto min-h-0">
                    <DataTable
                        columns={columns}
                        data={paginatedHistory}
                        totalCount={sortedHistory.length}
                        page={page}
                        pageSize={pageSize}
                        onPageChange={setPage}
                        onPageSizeChange={setPageSize}
                        sorting={sorting}
                        onSortingChange={setSorting}
                        manualSorting={true}
                        isLoading={false}
                        isError={false}
                    />
                </div>
            </DialogContent>
        </Dialog>
    );
}